import { inject, ref } from 'vue';
import axios from 'axios';
import { config } from '@/config';
import { apolloService } from '@/apollo/apolloRequests.ts';
import {
  PeriodsSubfolderType,
  ContractsFileType, FileDto, AccountingFileType, Mutation,
} from '@/generated/graphql';
import { mutations } from '@/apollo';
import { Dialog, EventBus, Notify } from 'quasar';
import {
  FileInfo,
  FileEnumAccounting,
  type typeFileAccounting,
  FileActions,
  type typesSubfoldersForQuickMove, EventBusEvents,
} from '@/helpers/types.ts';
import UploadFileDialog from '@/components/FileManagementDialogs/UploadFileDialog.vue';
import ViewAccountingFileDialog from '@/components/FileManagementDialogs/ViewAccountingFileDialog.vue';
import ViewContractFileDialog from '@/components/FileManagementDialogs/ViewContractFileDialog.vue';
import RenameFileDialog from '@/components/FileManagementDialogs/RenameFileDialog.vue';
import QDialogDenyFileComponent from '@/components/ManagementAccountingFileStatus/QDialogDenyFileComponent.vue';
import { getToken } from '@/helpers/authUtils.ts';
import uriLoadHandler from '@/helpers/uriLoadHandler.ts';
import { useRetrievePeriodWithSubfolders } from '@/composables/useRetrievePeriodWithSubfolders';
import { DeniableFileType } from '@/components/ManagementAccountingFileStatus/types.ts';

export const useFilesActions = () => {
  const bus = inject<EventBus>('bus');
  const isLoadingQuickMove = ref<boolean>(false);

  const { fetchPeriodWithSubfolders } = useRetrievePeriodWithSubfolders();

  const openUploadDialog = (): Promise<File[]> => {
    const files = ref<File[]>([]);
    return new Promise((resolve) => {
      Dialog.create({
        component: UploadFileDialog,
        componentProps: {},
      }).onOk((payload) => {
        files.value = payload.localValue;
        resolve(files.value);
      });
    });
  };

  const upload = async (
    dataFiles: File[],
    filesType: typeFileAccounting,
    folderId: string,
    companyId: string,
  ): Promise<void> => {
    const data = new FormData();
    data.append('companyId', companyId);
    dataFiles.forEach((el) => data.append('files', el));
    try {
      const result = await axios({
        method: 'POST',
        url: `${config.filestorage}`,
        headers: {
          'Content-Type': 'multipart/form-data',
          'enty-auth': getToken(),
        },
        data,
      });
      const savedFilesInFileStorage: FileInfo[] = result?.data;
      const promises = savedFilesInFileStorage.map((item) => {
        if (filesType === FileEnumAccounting.ContractsFileType) {
          const variables = {
            companyId,
            files: {
              fileId: item.id,
              name: item.name,
              contractsFolderId: folderId,
            },
          };
          return apolloService.makeApolloRequest(
            'mutation',
            mutations.SAVE_FILES_IN_CONTRACTS,
            variables,
            FileActions.Upload,
          );
        }
        if (filesType === FileEnumAccounting.AccountingFileType) {
          const variables = {
            companyId,
            files: {
              fileId: item.id,
              name: item.name,
              comment: '',
              periodsSubfolderId: folderId,
            },
          };
          return apolloService.makeApolloRequest(
            'mutation',
            mutations.SAVE_FILES_SUBFODLER,
            variables,
            FileActions.Upload,
          );
        }
        return null;
      });
      await Promise.all(promises);
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const transferTicketFileToPeriodFolder = async (
    dataFile: FileDto,
    subFolderId: string,
    companyId: string,
  ): Promise<Array<AccountingFileType>> => {
    const variables = {
      companyId,
      files: {
        fileId: dataFile.id,
        name: dataFile.name,
        periodsSubfolderId: subFolderId,
      },
    };

    const data = await apolloService.makeApolloRequest(
      'mutation',
      mutations.SAVE_FILES_SUBFODLER,
      variables,
      FileActions.Upload,
      false,
    ) as Mutation;
    return data.accounting.saveFilesInSubfolderId;
  };

  const openViewContractFileDialog = async (
    fileId: string,
    fileName: string,
    fileIndex: number,
    filesArray: Array<ContractsFileType>,
  ): Promise<void> => {
    if ((!fileName || fileName === '') && (!fileId || fileId === '')) return;
    Dialog.create({
      component: ViewContractFileDialog,
      componentProps: {
        fileId,
        fileName,
        fileIndex,
        filesArray,
      },
    });
  };

  const openViewAccountingFileDialog = async (
    subfolder?: PeriodsSubfolderType,
    isTicketCreationAvailable?: boolean,
  ): Promise<void> => {
    Dialog.create({
      component: ViewAccountingFileDialog,
      componentProps: {
        subfolder,
        isTicketCreationAvailable,
      },
    });
  };

  const openRenameDialog = (
    fileName: string,
    fileId: string,
    onCloseCallback?: () => void,
  ): Promise<{ editableValue: string; fileId: string }> => {
    return new Promise((resolve) => {
      const dialog = Dialog.create({
        component: RenameFileDialog,
        componentProps: {
          fileName,
          fileId,
        },
      });

      dialog.onOk(async (payload) => {
        await renameFile(payload.fileId, payload.editableValue);
        resolve({ editableValue: payload.editableValue, fileId: payload.fileId });
      });

      dialog.onDismiss(() => {
        if (onCloseCallback) onCloseCallback();
      });
    });
  };

  const openDenyFileComponentDialog = (
    file: DeniableFileType | DeniableFileType[],
    source?: FileEnumAccounting,
    onCloseCallback?: () => void,
  ): Promise<{ editableValue: string; fileId: string }> => {
    return new Promise(() => {
      const files = Array.isArray(file) ? file : [file];
      const dialog = Dialog.create({
        component: QDialogDenyFileComponent,
        componentProps: {
          files,
          source,
        },
      });

      dialog.onOk(async (payload) => {
        if (!bus || !payload) return;
        bus.emit(EventBusEvents.changedAccountingFiles, payload.fileId);
        bus.emit(EventBusEvents.updateTransferFiles);
      });

      dialog.onDismiss(() => {
        if (onCloseCallback) onCloseCallback();
      });
    });
  };

  const renameFile = async (fileId: string, fileName: string) => {
    if ((!fileName || fileName === '') && (!fileId || fileId === '')) {
      return;
    }
    await apolloService.makeApolloRequest(
      'mutation',
      mutations.RENAME_FILE,
      {
        renameFilePassword: 'renameFile',
        fileId,
        name: fileName,
      },
      FileActions.Rename,
    );
  };

  const download = async (fileId: string, fileName: string) => {
    if (!fileId) return;
    await uriLoadHandler(`${config.filestorage}/${fileId}`, fileName ?? 'file');
  };

  const downloadArchive = async (fileIds: string[]) => {
    if (!fileIds || fileIds?.length === 0) return;
    try {
      await apolloService.makeApolloRequest('mutation', mutations.DOWNLOAD_ARCHIVE, {
        fileIds,
      });

      // await uriLoadHandler(`${config.filestorage}/${(data as Mutation).archive}`, 'archive');
      Notify.create({
        message: 'Archive is generating. Please wait several minutes.',
        color: 'positive',
        position: 'top',
      });
    } catch (error) {
      console.error('Error downloading archive:', error);
    }
  };

  const updateReadStatus = async (fileIds: string[], isRead: boolean) => {
    if (!fileIds || fileIds.length === 0) return;
    try {
      const promises = fileIds.map((fileId) => {
        return apolloService.makeApolloRequest('mutation', mutations.EDIT_FILE_IS_READ, {
          fileId,
          isRead,
        });
      });

      await Promise.all(promises);
    } catch (error) {
      console.error('Error changing read status:', error);
      throw error;
    }
  };

  const buildHardRemoveDialogOptions = (fileCount: number) => {
    const isMany = fileCount > 1;
    return {
      title: isMany ? 'Delete files' : 'Delete file',
      message: isMany
        ? `Are you sure you want to delete ${fileCount} files?`
        : 'Are you sure you want to delete this file?',
    } as const;
  };

  const openConfirmDeleteFilesDialog = (fileIds: string[]): Promise<string[]> => {
    if (!fileIds || fileIds.length === 0) return Promise.resolve([]);
    const { title, message } = buildHardRemoveDialogOptions(fileIds.length);
    return new Promise((resolve) => {
      const dialog = Dialog.create({
        title,
        message,
        ok: { label: 'Delete', color: 'negative', flat: true, noCaps: true },
        cancel: { label: 'Cancel', flat: true, noCaps: true },
      });

      let settled = false;
      const tryResolve = (value: string[]) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      dialog.onOk(() => {
        tryResolve(fileIds);
      });
    });
  };

  const hardRemoveFiles = async (fileIds: string[]) => {
    if (!fileIds || fileIds?.length === 0) return;
    const deletePromises = fileIds.map(async (id) => {
      await apolloService.makeApolloRequest(
        'mutation',
        mutations.DELETE_FILE,
        {
          fileId: id,
        },
        FileActions.HardDelete,
      );
    });

    await Promise.all(deletePromises);
  };

  const requestDeleteFileContract = async (fileIds: string[]) => {
    if (!fileIds || fileIds?.length === 0) return;
    try {
      const deletePromises = fileIds.map(async (id) => {
        await apolloService.makeApolloRequest(
          'mutation',
          mutations.DELETE_CONTRACT_FILE,
          {
            contractId: id,
          },
          FileActions.DeleteContractFile,
        );
      });
      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error deleting contract:', error);
      throw error;
    }
  };

  const quickMoveFile = async (
    periodFolderId: string,
    typeFile: typesSubfoldersForQuickMove,
    fileId: string,
  ) => {
    if (!periodFolderId || !typeFile || !fileId) return;
    isLoadingQuickMove.value = true;
    const moveId = await getQuickFolderId(periodFolderId, typeFile);
    if (!moveId) return;
    try {
      await apolloService.makeApolloRequest(
        'mutation',
        mutations.MOVE_FILE,
        {
          moveFilePassword: 'moveFile',
          folderId: moveId,
          moveFileId: fileId,
        },
        FileActions.QuickMove,
      );
    } catch (error) {
      console.error('Error moving file:', error);
      throw error;
    } finally {
      isLoadingQuickMove.value = false;
    }
  };

  const getQuickFolderId = async (
    periodFolderId: string,
    typeFile: typesSubfoldersForQuickMove,
  ): Promise<string> => {
    try {
      let periodSubFolders = <PeriodsSubfolderType[]>[];
      const data = await fetchPeriodWithSubfolders(periodFolderId);
      if (data?.accounting?.getPeriodWithSubfolders?.subfolders) {
        periodSubFolders = data.accounting.getPeriodWithSubfolders.subfolders;
        const matchingSubfolder = periodSubFolders.find((subfolder) => subfolder.type === typeFile);
        return matchingSubfolder ? matchingSubfolder.id : '';
      }
      return '';
    } catch (error) {
      console.error('Error fetching period data:', error);
      throw error;
    }
  };

  return {
    isLoadingQuickMove,
    openUploadDialog,
    upload,
    openViewContractFileDialog,
    openViewAccountingFileDialog,
    renameFile,
    download,
    quickMoveFile,
    openRenameDialog,
    downloadArchive,
    openConfirmDeleteFilesDialog,
    hardRemoveFiles,
    updateReadStatus,
    requestDeleteFileContract,
    openDenyFileComponentDialog,
    transferTicketFileToPeriodFolder,
  };
};
